/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import {
  AfterViewInit,
  Component,
  effect,
  inject,
  input,
  output,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  CalendarResourcesDay,
  CommonUtils,
  LoaderService,
} from '@infodata-group/inf-commons';
import dayjs from 'dayjs';
import { DayNotWorkService } from '../day-not-work.service';

@Component({
  selector: 'infl-calendar-days',
  templateUrl: './calendar-days.component.html',
  styleUrls: ['./calendar-days.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [],
})
export class CalendarDaysComponent implements AfterViewInit {
  public date = input<Date>();
  public withGroup = input(false);
  public grayedCells = input<Date[]>();

  public loadSections = output<CalendarResourcesDay[]>();

  public sections = signal<CalendarResourcesDay[]>(null);
  private isLoaded = false;

  private readonly loader = inject(LoaderService);
  private readonly dayNotWorkService = inject(DayNotWorkService);

  constructor() {
    this.loader.startLoad();
    effect(() => {
      if (this.date()) {
        this.initSections();
        if (this.isLoaded) {
          setTimeout(() => {
            this.scrollToToday();
          }, 850);
        }
      }
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.scrollToToday();
      this.isLoaded = true;
      this.loader.stopLoad();
    }, 850);
  }

  private initSections() {
    let date = dayjs(this.date()).startOf('month');
    const actualMonth = date.month();
    const nextMonth = (actualMonth + 1) % 12;
    const sections = [];
    while (date.month() === actualMonth || date.month() === nextMonth) {
      const section = new CalendarResourcesDay(date.toDate());
      section.backgroundColor = this.dayNotWorkService.isDayWork(date.toDate())
        ? 'var(--white)'
        : this.dayNotWorkService.dayNotWorkColor;
      sections.push(section);
      if (
        this.grayedCells().find((grayedCell) =>
          CommonUtils.compareDate(grayedCell, date.toDate()),
        )
      ) {
        section.grayed = true;
      }
      date = dayjs(date).add(1, 'd');
    }
    this.loadSections.emit(sections);
    this.sections.set(sections);
  }

  private scrollToToday() {
    const container = document.querySelector(
      '.calendar-container',
    ) as HTMLElement;
    if (!container) {
      return;
    }

    const sections = Array.from(
      container.getElementsByClassName('section-agenda'),
    ) as HTMLElement[];
    const currentSection = sections.find((section) =>
      section.classList.contains('current'),
    );
    if (!currentSection) {
      container.scrollTo({ top: 0, behavior: 'instant' });
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      let height = 0;
      for (const section of sections) {
        if (section === currentSection) {
          break;
        }
        height += section.clientHeight;
      }
      container.scrollTo({ top: height, behavior: 'instant' });
      resizeObserver.disconnect();
    });

    resizeObserver.observe(currentSection);
  }
}
